/** Upload progress bars for active uploads (storage page and legacy storage drawer) */

import { X } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { useAppStore, type UploadQueueItem } from '../../stores/app-store'

/** `isDark` is ignored (theme tokens); kept so legacy callers still compile */
export function UploadProgressList({ className }: { isDark?: boolean; className?: string }) {
  const t = useT()
  const { uploadQueue, removeFromUploadQueue } = useAppStore()

  const activeUploads = uploadQueue.filter((u) => u.status !== 'complete')
  if (!activeUploads.length) return null

  return (
    <ul className={cn('space-y-1.5', className ?? 'px-3 pb-2')} aria-label={t('storage.upload.progressList')}>
      {activeUploads.map((item) => (
        <UploadProgressItem key={item.id} item={item} onRemove={removeFromUploadQueue} />
      ))}
    </ul>
  )
}

function UploadProgressItem({ item, onRemove }: { item: UploadQueueItem; onRemove: (id: string) => void }) {
  const t = useT()
  const isError = item.status === 'error'

  return (
    <li className={cn('rounded-md border bg-surface-raised p-2', isError ? 'border-danger/30' : 'border-border')}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="truncate text-xs font-medium text-fg">{item.file.name}</span>
        {isError && (
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            aria-label={t('storage.upload.dismissNamed', { name: item.file.name })}
            className="cursor-pointer rounded p-0.5 text-fg-subtle hover:bg-surface-hover hover:text-fg"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-sm bg-surface-muted"
        role="progressbar"
        aria-label={t('storage.upload.progressNamed', { name: item.file.name })}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={item.progress}
      >
        <div
          className={cn('h-full transition-all duration-300', isError ? 'bg-danger' : 'bg-accent')}
          style={{ width: `${item.progress}%` }}
        />
      </div>
      {isError && (
        <p role="alert" className="mt-1 text-xs text-danger">
          {item.error ?? t('storage.upload.failed')}
        </p>
      )}
    </li>
  )
}
